import { type Role } from 'components/Permission/permission.types';
import { appConfiguration } from 'utils/config/app.config';
interface NavigationParams {
  path: string;
  displayName: string;
  roles: Role[];
  iconName?: string;
}
interface HiddenNavigationParams {
  roles: Role[];
  path: string;
}

interface INavigationMap {
  fleetOverview: {
    fleetSummary: NavigationParams;
    oldLandingPage: NavigationParams;
    vehicleStatus: NavigationParams;
  };
  dtcSummary: NavigationParams;
  engineHoursSummary: NavigationParams;
  sensorAnalysis: NavigationParams;

  notifications: NavigationParams;
  driverBehavior: {
    overview: NavigationParams;
    inclineRoadCondition: NavigationParams;
  };
  networkHealth: NavigationParams;
  roadConditionSummary: NavigationParams;
  insights: NavigationParams;
  utilization: {
    overview: NavigationParams;
    idlingAnalysis: NavigationParams;
  };
  fuelAnalytics: {
    overview: NavigationParams;
  };
  production: {
    overview: NavigationParams;
    loadCycleAnalysis: NavigationParams;
  };
  tirePressure: {
    notifications: NavigationParams;
    fleet: NavigationParams;
    vahicle: NavigationParams;
    leakageAnalysis: NavigationParams;
  };
  customerSelectionView: NavigationParams;
  settings: {
    internalUsers: NavigationParams;
    externalUsers: NavigationParams;
    customers: NavigationParams;
    contactInformation: NavigationParams;
    rules: NavigationParams;
    // localization: NavigationParams;
    tags: NavigationParams;
    poc: NavigationParams;
    driverBehavior: NavigationParams;
  };
  reports: HiddenNavigationParams;
  squares: {
    dashboard: NavigationParams;
    costAnalysis: NavigationParams;
    alerts: NavigationParams;
    maintenance: NavigationParams;
    workOrders: NavigationParams;
    uploadWorkOrders: NavigationParams;
    survivalAnalysis: NavigationParams;
  };
  survivalAnalysis: {
    overview: NavigationParams;
    prognosis: NavigationParams;
  };
  development?: NavigationParams;
}

/*
  Source of truth for all routes, including nested routes
*/
export const navigationMap: INavigationMap = {
  fleetOverview: {
    fleetSummary: {
      path: '/fleet-summary',
      displayName: 'Fleet Summary',
      iconName: 'sidebarFleetOverview2',
      roles: [], // page is visible to any role
    },
    oldLandingPage: {
      path: '/fleet-summary-old',
      displayName: 'Landing page',
      iconName: 'sidebarFleetOverview2',
      roles: [], // page is visible to any role
    },
    vehicleStatus: {
      path: '/vehicle-status',
      displayName: 'Vehicle Status',
      iconName: 'sidebarFleetOverview2',
      roles: [],
    },
  },

  dtcSummary: {
    path: '/diagnostic-trouble-codes',
    displayName: 'Diagnostic Trouble Codes',
    iconName: 'sidebarFleetOverview2',
    roles: [],
  },

  engineHoursSummary: {
    path: '/engine-hours-summary',
    displayName: 'Activity Tracker',
    iconName: 'sidebarEngineHours',
    roles: [],
  },

  sensorAnalysis: {
    path: '/sensor-analysis',
    displayName: 'Sensor Analysis',
    iconName: 'sidebarVehicleStatus',
    roles: [],
  },

  notifications: {
    path: '/notifications',
    displayName: 'Notification Center',
    iconName: 'sidebarNotificationCenter',
    roles: [],
  },
  driverBehavior: {
    overview: {
      path: '/driver-behavior/overview',
      displayName: 'Overview',
      iconName: 'driverBehavior',
      roles: [],
    },
    inclineRoadCondition: {
      path: '/terrain-view',
      displayName: 'Terrain View',
      iconName: 'driverBehavior',
      roles: [],
    },
  },
  networkHealth: {
    path: '/network-health',
    displayName: 'Network Health',
    iconName: 'networkHealth',
    roles: [],
  },
  roadConditionSummary: {
    path: '/road-condition-summary',
    displayName: 'Road Condition Summary',
    iconName: 'sidebarRoadCondition',
    roles: [],
  },
  utilization: {
    overview: {
      path: '/utilization/overview',
      displayName: 'Overview',
      iconName: 'sidebarUtilization',
      roles: ['internal-user', 'director', 'external-admin', 'external-user'],
    },
    idlingAnalysis: {
      path: '/utilization/analysis',
      displayName: 'Idle Analysis',
      iconName: 'sidebarUtilization',
      roles: ['internal-user', 'director', 'external-admin', 'external-user'],
    },
  },
  fuelAnalytics: {
    overview: {
      path: '/fuelUsage/overview',
      displayName: 'Overview',
      iconName: 'sidebarFuelUsage2',
      roles: ['internal-user', 'director', 'external-admin', 'external-user'],
    },
  },
  production: {
    overview: {
      path: '/production/overview',
      displayName: 'Overview',
      iconName: 'sidebarProduction',
      roles: ['internal-user', 'director', 'external-admin', 'external-user'],
    },
    loadCycleAnalysis: {
      path: '/production/load-cycle-analysis/:assetVin',
      displayName: 'Load Cycle Analysis',
      iconName: 'sidebarProduction',
      roles: ['internal-user', 'director', 'external-admin', 'external-user'],
    },
  },
  insights: {
    path: '/insights',
    displayName: 'Insights',
    iconName: 'mlInsightsCondition',
    roles: [],
  },
  tirePressure: {
    notifications: {
      path: '/tire-analytics/notifications',
      displayName: 'Notifications',
      iconName: 'sidebarProduction',
      roles: ['internal-user', 'director', 'external-admin', 'external-user'],
    },
    fleet: {
      path: '/tire-analytics/fleet-insight',
      displayName: 'Fleet Insight',
      iconName: 'sidebarProduction',
      roles: ['internal-user', 'director', 'external-admin', 'external-user'],
    },
    vahicle: {
      path: '/tire-analytics/vehicle-insight',
      displayName: 'Vehicle Insight',
      iconName: 'sidebarProduction',
      roles: ['internal-user', 'director', 'external-admin', 'external-user'],
    },
    leakageAnalysis: {
      path: '/tire-analytics/leakage-analysis',
      displayName: 'Leakage Analysis',
      iconName: 'sidebarProduction',
      roles: ['internal-user', 'director', 'external-admin', 'external-user'],
    },
  },
  customerSelectionView: {
    path: '/customer-selection',
    displayName: 'Customer Selection',
    roles: ['director', 'internal-user'],
  },

  settings: {
    internalUsers: {
      path: '/settings/symboticware-user',
      displayName: 'Symboticware Users',
      roles: ['director', 'internal-user'],
    },
    externalUsers: {
      path: '/settings/user',
      displayName: 'Users & Assets',
      roles: ['internal-user', 'external-admin', 'director'],
    },
    customers: {
      path: '/settings/customer-accounts',
      displayName: 'Customer Accounts',
      roles: ['director'],
    },
    contactInformation: {
      path: '/settings/contact-information',
      displayName: 'Contact Information',
      roles: ['director', 'internal-user', 'external-admin'],
    },
    rules: {
      path: '/settings/rules',
      displayName: 'Rules',
      roles: ['internal-user', 'external-admin', 'external-user', 'director'],
    },
    // localization: {
    //   path: '/settings/localization',
    //   displayName: 'Localization Settings',
    //   roles: [],
    // },
    tags: {
      path: '/settings/tags',
      displayName: 'Tags',
      roles: ['director', 'internal-user'],
    },
    poc: {
      path: '/settings/poc',
      displayName: 'POC',
      roles: ['internal-user', 'external-admin', 'external-user', 'director'],
    },
    driverBehavior: {
      path: '/settings/driver-behavior',
      displayName: 'Driver Behavior',
      roles: ['director', 'internal-user', 'external-admin'],
    },
  },
  reports: {
    path: '/reports/productivity',
    roles: ['internal-user', 'external-admin', 'director'],
  },
  squares: {
    maintenance: {
      path: '/squares/maintenance',
      displayName: 'Maintenance',
      roles: ['director', 'external-admin', 'internal-user', 'developer'],
    },
    dashboard: {
      path: '/squares/dashboard',
      displayName: 'Dashboard',
      roles: [
        'director',
        'external-admin',
        'external-user',
        'internal-user',
        'developer',
      ],
    },
    costAnalysis: {
      path: '/squares/costAnalysis',
      displayName: 'Insights and Recommendations',
      roles: [
        'director',
        'external-admin',
        'external-user',
        'internal-user',
        'developer',
      ],
    },
    alerts: {
      path: '/squares/alerts',
      displayName: 'Alerts',
      roles: ['director', 'external-admin', 'internal-user', 'developer'],
    },
    workOrders: {
      path: '/squares/workOrder',
      displayName: 'Work Orders',
      roles: [
        'director',
        'external-admin',
        'external-user',
        'internal-user',
        'developer',
      ],
    },
    uploadWorkOrders: {
      path: '/squares/uploadWorkOrders',
      displayName: 'Upload Work Orders',
      roles: ['director', 'external-admin', 'internal-user', 'developer'],
    },
    survivalAnalysis: {
      path: '/squares/survivalAnalysis',
      displayName: 'Survival Analysis',
      roles: [
        'director',
        'external-admin',
        'external-user',
        'internal-user',
        'developer',
      ],
    },
    // ReportsView: {
    //   path: '/squares/Reports',
    //   displayName: 'Reports',
    //   roles: ['director', 'external-admin'],
    // },
  },
  survivalAnalysis: {
    overview: {
      path: '/survivalAnalysis/overview',
      displayName: 'Overview',
      roles: ['director', 'external-admin'],
    },
    prognosis: {
      path: '/survivalAnalysis/prognosis',
      displayName: 'Prognosis',
      roles: ['director', 'external-admin'],
    },
  },
  ...(appConfiguration.isDevelopment && {
    development: {
      path: '/playground',
      displayName: 'Playground',
      iconName: 'sidebarFleetOverview',
      roles: ['developer'],
    },
  }),
};
